import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EventModel, EventSubStatus } from './event.model';
import { EventExportModel } from './event-export.model';
import { AbstractEntityService } from '@app/shared/abstract/abstract-entity.service';
import { Observable } from 'rxjs';
import { ResourceData } from '@app/shared/abstract/resource.data';
import { ProductModel } from '@app/products/shared/product.model';
import { NoteModel } from '@app/events/shared/note.model';
import { EventProductItemModel } from '@app/events/shared/event-product-item.model';
import { EventTimelineItemModel } from '@app/events/shared/event-timeline-item.model';
import { ReminderModel } from '@app/events/shared/reminder.model';

/**
 * TVG-331: one event a viewing can be booked on, as returned by the Deals
 * board's "Book New Viewing" picker.
 */
export interface BookableViewingEvent {
  id: number;
  name: string | null;
  reference: string | null;
  status: string;
  start_date: string | null;
  show_round: string | null;
  venue: { id: number; name: string } | null;
}

/** TVG-331: a contact with at least one bookable event, at any venue. */
export interface BookableViewingContact {
  id: number;
  name: string;
  email: string | null;
  events: BookableViewingEvent[];
}

export interface FinancialSummary {
  venue_hire_total: number;
  accommodation_total: number;
  hire_price: number;
  minimum_spend: number;
  minimum_spend_total: number;
  total_products_value: number;  // Excludes venue hire and accommodation
  adjustments_total: number;  // TVG-156: net of admin discount/adjustment lines
  grand_total: number;  // VG-415: Venue Hire + Accommodation + Products + adjustments
  total_base_price: number;  // Excludes venue hire and accommodation
  total_addons: number;  // Excludes venue hire and accommodation
  minimum_spend_left: number;
  minimum_spend_contractual: number | null;
  minimum_spend_contractual_left: number;
  total_payments_due: number;
  total_payments_paid: number;
  total_payments_outstanding: number;
  overdue_payments: number;
  unabsorbed_credit: number;
  excess_credits_available: number;
  contractual_spend: number;
  contractual_spend_left: number;
  total_extras_value: number;
  extras_contractual_spend: number;
  extras_contractual_spend_left: number;
  total_dj_value: number;
  dj_contractual_spend: number;
  dj_contractual_spend_left: number;
  qualifying_total: number;
  offer_eligible: boolean;
  offer_discount_applied: number;
  offer_item_candidates: EventProductItemModel[];
  current_offer_item_ids?: number[];
  blocked_offer_item_ids?: number[];
}


// TVG-122: Successful Move-to-Pencil payload shape.
export interface PencilDraftSibling {
  id: number;
  reference: string | null;
  name: string | null;
  status: string;
}
export interface MoveToPencilResponse {
  data: EventModel;
  other_drafts: PencilDraftSibling[];
}
// TVG-122: 409 conflict body returned when the contact already has another
// Pencilled event. The frontend re-issues the request with
// acknowledge_concurrent_pencil=true to override.
export interface PencilConflictPayload {
  event_id: number;
  event_reference: string | null;
  contact_name: string;
}

@Injectable({
  providedIn: 'root',
})
export class EventService extends AbstractEntityService<EventModel> {
  constructor(http: HttpClient) {
    super('/api/events', http);
  }

  fieldIsUnique(field: string, value: string): Observable<{ is_unique: boolean }> {
    return this.http.get<{ is_unique: boolean }>(`${this.apiUrl}${this.basePath}/unique?field=${field}&value=${value}`);
  }

  // TVG-152: Event CSV export now runs as a background job (gated behind the
  // "event-export" feature toggle on the backend). Request the export, poll the
  // returned record until it is ready, then download the stored file.
  requestExport(): Observable<ResourceData<EventExportModel>> {
    return this.http.post<ResourceData<EventExportModel>>(`${this.apiUrl}${this.basePath}/exports`, {});
  }

  getExport(exportId: number): Observable<ResourceData<EventExportModel>> {
    return this.http.get<ResourceData<EventExportModel>>(`${this.apiUrl}${this.basePath}/exports/${exportId}`);
  }

  downloadExport(exportId: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}${this.basePath}/exports/${exportId}/download`, {
      responseType: 'blob',
    });
  }

  saveFormData(formData: FormData, eventId?: number | null): Observable<ResourceData<EventModel>> {
    if (eventId) {
      return this.http.post<ResourceData<EventModel>>(`${this.apiUrl}${this.basePath}/${eventId}`, formData);
    } else {
      return this.http.post<ResourceData<EventModel>>(`${this.apiUrl}${this.basePath}`, formData);
    }
  }

  updateAllergies(eventId: number, data: { has_guest_allergies: boolean; guest_allergies: string }): Observable<ResourceData<EventModel>> {
    return this.http.put<ResourceData<EventModel>>(`${this.apiUrl}${this.basePath}/${eventId}/allergies`, data);
  }

  getOverview(eventId: number | string): Observable<ResourceData<EventModel>> {
    return this.http.get<ResourceData<EventModel>>(`${this.apiUrl}${this.basePath}/${eventId}/overview`);
  }

  getTimelineItems(eventId: number | string): Observable<{ data: EventTimelineItemModel[] }> {
    return this.http.get<{ data: EventTimelineItemModel[] }>(`${this.apiUrl}${this.basePath}/${eventId}/timeline-items`);
  }

  getEventNotes(eventId: number | string): Observable<{ data: NoteModel[] }> {
    return this.http.get<{ data: NoteModel[] }>(`${this.apiUrl}${this.basePath}/${eventId}/notes`);
  }

  getEventReminders(eventId: number | string): Observable<{ data: ReminderModel[] }> {
    return this.http.get<{ data: ReminderModel[] }>(`${this.apiUrl}${this.basePath}/${eventId}/reminders`);
  }

  createEventNote(data: any) {
    const formData = new FormData();

    Object.keys(data).forEach(key => {
      if (key === 'attachment' && data[key] instanceof File) {
        formData.append(key, data[key], data[key].name);
      } else if (data[key] !== null) {
        formData.append(key, data[key]);
      }
    });

    return this.http.post<ResourceData<NoteModel>>(`${this.apiUrl}/api/notes`, formData);
  }

  updateEventNote(data: any, eventNoteId: number) {
    const formData = new FormData();

    Object.keys(data).forEach(key => {
      if (key === 'attachment' && data[key] instanceof File) {
        formData.append(key, data[key], data[key].name);
      } else if (key === 'remove_attachment' && data[key]) {
        formData.append(key, data[key]);
      } else if (key === 'attachment') {
        // Skip attachment field if it's not a File instance
      } else if (data[key] !== null) {
        formData.append(key, data[key]);
      }
    });

    return this.http.post<ResourceData<NoteModel>>(`${this.apiUrl}/api/notes/${eventNoteId}`, formData);
  }

  deleteEventNote(deleteNoteId: number) {
    return this.http.delete<ResourceData<NoteModel>>(`${this.apiUrl}/api/notes/${deleteNoteId}`);
  }

  createEventReminder(data: any) {
    return this.http.post<ResourceData<ReminderModel>>(`${this.apiUrl}/api/reminders`, data);
  }

  deleteEventReminder(deleteReminderId: number) {
    return this.http.delete<ResourceData<ReminderModel>>(`${this.apiUrl}/api/reminders/${deleteReminderId}`);
  }

  // TVG-52: `sections` limits the PDF to the chosen logical sections (comma
  // separated). Omitting it returns the full event PDF.
  downloadPdf(eventId: number, sections?: string[]) {
    return this.http.get(`${this.apiUrl}${this.basePath}/${eventId}/pdf`, {
      responseType: 'blob',
      params: sections?.length ? { sections: sections.join(',') } : {},
    });
  }

  downloadKitchenPdf(eventId: number) {
    return this.http.get(`${this.apiUrl}${this.basePath}/${eventId}/kitchen-pdf`, { responseType: 'blob' });
  }

  // TVG-104: printable Table Plan pack (landscape room plan + portrait lists).
  downloadTablePlanPdf(eventId: number) {
    return this.http.get(`${this.apiUrl}${this.basePath}/${eventId}/table-plan-pdf`, { responseType: 'blob' });
  }


  createEventProductItem(data: any) {
    return this.http.post<ResourceData<EventProductItemModel>>(`${this.apiUrl}/api/event-product-items`, data);
  }

  updateEventProductItem(data: any, eventProductId: number) {
    return this.http.put<ResourceData<EventProductItemModel>>(`${this.apiUrl}/api/event-product-items/${eventProductId}`, data);
  }

  deleteEventProductItem(deleteProductItemId: number) {
    return this.http.delete<ResourceData<EventProductItemModel>>(
      `${this.apiUrl}/api/event-product-items/${deleteProductItemId}`
    );
  }

  createEventTimelineItem(data: any) {
    return this.http.post<ResourceData<EventTimelineItemModel>>(`${this.apiUrl}/api/event-timeline-items`, data);
  }

  updateEventTimelineItem(data: any, eventTimelineId: number) {
    return this.http.put<ResourceData<EventTimelineItemModel>>(
      `${this.apiUrl}/api/event-timeline-items/${eventTimelineId}`,
      data
    );
  }

  deleteEventTimelineItem(deleteTimelineItemId: number) {
    return this.http.delete<ResourceData<EventTimelineItemModel>>(
      `${this.apiUrl}/api/event-timeline-items/${deleteTimelineItemId}`
    );
  }

  getFinancialSummary(eventId: number): Observable<FinancialSummary> {
    return this.http.get<FinancialSummary>(`${this.apiUrl}/api/events/${eventId}/financial-summary`);
  }

  updateEventStatus(id: number, status: 'Provisional' | 'Opportunity' | 'Confirmed' | 'Cancelled' | 'Closed' | 'Lost') {
    return this.http.put<ResourceData<EventModel>>(`${this.apiUrl}/api/events/${id}/status`, { status });
  }

  // TVG-119: Flip the Active Opportunity sub-status. Reverse direction allowed.
  updateSubStatus(id: number, sub_status: EventSubStatus) {
    return this.http.patch<ResourceData<EventModel>>(`${this.apiUrl}/api/events/${id}/sub-status`, { sub_status });
  }

  // TVG-124: Mark an event as Lost with an optional reason.
  markAsLost(
    id: number,
    payload: { lost_reason?: string | null; lost_reason_other?: string | null } = {},
  ) {
    return this.http.post<ResourceData<EventModel>>(`${this.apiUrl}/api/events/${id}/mark-as-lost`, payload);
  }

  // TVG-123: Cancel a Confirmed booking. Admin/manager-only on the server;
  // mirrors the existing un-confirm rule on EventController::updateStatus.
  // Endpoint 404s when the `crm-sales-flow` toggle is off.
  cancelBooking(id: number) {
    return this.http.post<ResourceData<EventModel>>(`${this.apiUrl}/api/events/${id}/cancel-booking`, {});
  }

  // TVG-120: Clone an event into a sibling. Returns the new event resource.
  // Endpoint 404s when the `crm-sales-flow` toggle is off; the Clone Event
  // button is hidden in that case so the call should not happen.
  cloneEvent(id: number) {
    return this.http.post<ResourceData<EventModel>>(`${this.apiUrl}/api/events/${id}/clone`, {});
  }

  // TVG-121: Staff cancel viewing — clears show_round, writes a system log
  // entry, demotes the Kanban card.
  cancelViewing(id: number) {
    return this.http.post<ResourceData<EventModel>>(`${this.apiUrl}/api/events/${id}/viewing/cancel`, {});
  }

  // TVG-121: Staff "did not attend" — only valid once now >= show_round. Clears
  // show_round, writes a system log entry.
  markViewingNoShow(id: number) {
    return this.http.post<ResourceData<EventModel>>(`${this.apiUrl}/api/events/${id}/viewing/no-show`, {});
  }

  // TVG-134: Book a viewing from the Sales board (manual drag into Viewing
  // Booked). Sets show_round (date + optional time) and routes the card into
  // the Viewing Booked column.
  // TVG-379: the Book New Viewing dialog also names who will show them round;
  // left out, the event keeps the person it has.
  bookViewing(id: number, show_round_date: string, show_round_time: string | null = null, show_around_name_id?: number | null) {
    return this.http.post<ResourceData<EventModel>>(`${this.apiUrl}/api/events/${id}/viewing/book`, {
      show_round_date,
      show_round_time: show_round_time || null,
      ...(show_around_name_id === undefined ? {} : { show_around_name_id }),
    });
  }

  // TVG-331: Contacts with an event a viewing can be booked on, at any venue,
  // for the Deals board's "Book New Viewing" picker. Read-only — the booking
  // itself still goes through bookViewing().
  bookableViewingContacts(search: string) {
    return this.http.get<{ data: BookableViewingContact[] }>(`${this.apiUrl}/api/viewings/bookable`, {
      params: search ? { search } : {},
    });
  }

  // TVG-122: Move an event into Pencil (Provisional). The endpoint returns 409
  // with a `conflict` payload when the contact has another Pencilled event;
  // the caller re-issues with acknowledge_concurrent_pencil=true to override.
  // On success returns the updated event plus an `other_drafts` list (events
  // at Opportunity/Provisional on the same contact) for the clones prompt.
  moveToPencil(id: number, acknowledge_concurrent_pencil = false) {
    return this.http.post<MoveToPencilResponse>(
      `${this.apiUrl}/api/events/${id}/pencil`,
      { acknowledge_concurrent_pencil },
    );
  }

  // TVG-122: Release a Pencilled event back to Active Opportunity. Writes a
  // system log entry; date-block clears automatically via the existing
  // DATE_BLOCKING_STATUSES / Event::boot::saving path.
  releasePencil(id: number) {
    return this.http.post<ResourceData<EventModel>>(`${this.apiUrl}/api/events/${id}/pencil/release`, {});
  }

  updateShowOnBoard(id: number, show_on_board: boolean) {
    return this.http.put<ResourceData<EventModel>>(`${this.apiUrl}/api/events/${id}/show-on-board`, { show_on_board });
  }

  downloadNoteAttachment(noteId: number) {
    return this.http.get(`${this.apiUrl}/api/notes/${noteId}/attachment`, {
      responseType: 'blob',
      observe: 'response'
    });
  }

  updatePaymentSync(id: number, payment_sync_enabled: boolean) {
    return this.http.put<{ message: string }>(`${this.apiUrl}/api/events/${id}/payment-sync`, { payment_sync_enabled });
  }

  getFinancialBreakdown(eventId: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/api/events/${eventId}/financial-breakdown`);
  }

  getCalendarEvents(startDate?: string, endDate?: string, status?: string): Observable<{data: any[]}> {
    const params: any = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (status) params.status = status;

    return this.http.get<{data: any[]}>(`${this.apiUrl}/api/events/calendar`, { params });
  }

  getChoices(search?: string, page?: number, limit?: number): Observable<{ options: { value: number; label: string }[], hasMore: boolean }> {
    const params: any = {};
    if (search) params.search = search;
    if (page) params.page = page;
    if (limit) params.limit = limit;

    return this.http.get<{ options: { value: number; label: string }[], hasMore: boolean }>(`${this.apiUrl}/api/events/choices`, { params });
  }
}
